/**
 * CUBE JUMP — isometric pyramid painter (MVP step 1 — skeleton + texture).
 *
 * Heritage: Q*bert (1982). Coffee-bean player hops diagonally between
 * cube tops on a 7-row pyramid (28 cubes total). Landing on an unpainted
 * cube paints it. Clear all 28 → level-clear card + 90° pyramid rotation
 * + paint reset. Step 1 has **no falls, no enemies, no game-over**;
 * the focus is to ship a complete-feeling FOUNDATION with the texture
 * baked in (attract loop, idle-wiggle, paint flash, level-clear card).
 *
 * Coordinate system
 * -----------------
 * The pyramid is a 7-row triangular grid. We address cubes by `(row, col)`
 * where row 0 is the top cube and row 6 is the bottom row of 7 cubes.
 * Row r has (r+1) cubes; col runs 0..r.
 *
 * Diagonal moves on the grid:
 *   NE (up-right):   (r, c) → (r-1, c)
 *   NW (up-left):    (r, c) → (r-1, c-1)
 *   SE (down-right): (r, c) → (r+1, c+1)
 *   SW (down-left):  (r, c) → (r+1, c)
 *
 * In step 1, moves that would land off the pyramid are CLAMPED (refused)
 * rather than triggering a fall. Falls land in step 2 along with lives,
 * game-over, and the CRT-glitch death effect.
 *
 * Iso projection
 * --------------
 * Each cube is rendered as a stack of three faces (top diamond + left
 * parallelogram + right parallelogram). The cube origin in pyramid space
 * is computed from (row, col) and projected to screen using a fixed
 * iso basis. Drawn back-to-front (row 0 first) so depth sorting is
 * implicit.
 *
 * Landscape rule
 * --------------
 * The pyramid is portrait-feel. Per the cabinet's landscape-only rule,
 * the playfield sits in a centered ~270-wide column with cartridge-owned
 * HUD chrome in the left + right gutters (score / lives / level pips).
 * No black bars.
 *
 * Determinism
 * -----------
 * Step 1 has no PRNG-driven state. The pyramid rotation is deterministic
 * (90° per level), idle-wiggle is a sine of the world tick, and the
 * attract-mode demo loop is a hard-coded scripted sequence. Step 3 will
 * introduce ctx.random for snake-spawn timing.
 */

import type {
  GameContext,
  GameInstance,
  InputState,
} from '@arcraz/arcadia-types';
import {
  ATTRACT_DIM,
  ATTRACT_PROMPT,
  ATTRACT_TITLE,
  BALL_BODY,
  BALL_DARK,
  BALL_HIGHLIGHT,
  BALL_WARNING,
  BALL_WARNING_GLOW,
  BETA_CUBE_LEFT_PAINTED,
  BETA_CUBE_LEFT_UNPAINTED,
  BETA_CUBE_RIGHT_PAINTED,
  BETA_CUBE_RIGHT_UNPAINTED,
  BETA_CUBE_TOP_PAINTED,
  BETA_CUBE_TOP_UNPAINTED,
  BETA_GRID_GLOW,
  BETA_GRID_LINE,
  BETA_GUTTER_BG,
  BETA_GUTTER_EDGE,
  BETA_SKY_BOTTOM,
  BETA_SKY_TOP,
  BEAN_BODY,
  BEAN_CREASE,
  BEAN_HIGHLIGHT,
  BEAN_OUTLINE,
  CARD_BG,
  CARD_BORDER,
  CARD_SUB,
  CARD_TEXT,
  CUBE_LEFT_PAINTED,
  CUBE_LEFT_UNPAINTED,
  CUBE_OUTLINE,
  CUBE_RIGHT_PAINTED,
  CUBE_RIGHT_UNPAINTED,
  CUBE_TOP_FLASH,
  CUBE_TOP_PAINTED,
  CUBE_TOP_UNPAINTED,
  DANGER_BG,
  DANGER_BORDER,
  DANGER_SUB,
  DANGER_TEXT,
  GRID_GLOW,
  GRID_LINE,
  GUTTER_BG,
  GUTTER_EDGE,
  HUD_LABEL,
  HUD_LEVEL,
  HUD_LIVES,
  HUD_SCORE,
  SKY_BOTTOM,
  SKY_TOP,
  SNAKE_BODY,
  SNAKE_DARK,
  SNAKE_EYE,
  SNAKE_FORKED_TONGUE,
  SNAKE_HIGHLIGHT,
} from './palette.js';

// ---- Canvas + playfield geometry ----
const WIDTH = 480;
const HEIGHT = 270;

// Centered playfield column (landscape-only rule: side gutters host HUD,
// not black bars). 280 wide so the pyramid has breathing room and the
// gutters are wide enough for legible HUD chrome.
const PLAYFIELD_W = 280;
const PLAYFIELD_X = (WIDTH - PLAYFIELD_W) / 2; // 100
const GUTTER_W = PLAYFIELD_X;                   // 100 each side

// ---- Pyramid geometry ----
const PYRAMID_ROWS = 7;
// Total cubes on a 7-row triangular pyramid is 1+2+...+7 = 28.
// (Computed only for documentation; the cubes array length is the
// authoritative source at runtime.)

// Iso cube dimensions in screen pixels.
const CUBE_W = 28;      // top diamond width
const CUBE_H = 14;      // top diamond half-height (so full diamond = 28×14)
const CUBE_DEPTH = 14;  // side-face vertical depth

// Row spacing — each row down shifts by CUBE_H (so adjacent rows visually
// stack in iso) and a row's cubes are spaced CUBE_W apart horizontally.
const ROW_DY = CUBE_H + CUBE_DEPTH; // 28
const COL_DX = CUBE_W;              // 28

// Pyramid apex screen position. Centered horizontally in the playfield;
// vertical position picked so all 7 rows fit between the top HUD strip
// (22px) and the bottom of the canvas with breathing room. Bottom row
// origin at y = 38 + 6×28 = 206; cube depth extends to y = 206 + 28 = 234;
// leaves 36px of bottom margin so the side faces never clip the bezel.
const PYRAMID_APEX_X = PLAYFIELD_X + PLAYFIELD_W / 2;
const PYRAMID_APEX_Y = 38;

// ---- Bean (player) ----
const BEAN_W = 16;
const BEAN_H = 18;
// Vertical offset of the bean's feet above the cube's top-center point.
// Negative because screen Y grows downward and we want the bean above.
const BEAN_FOOT_OFFSET_Y = -6;

// Jump animation — the bean lifts in an arc between cubes. ARC_TICKS is
// fast enough to feel snappy but slow enough that the player reads the
// arc as deliberate.
const JUMP_ARC_TICKS = 18;
const JUMP_ARC_HEIGHT = 18; // pixels of lift at the apex

// ---- Paint flash ----
const PAINT_FLASH_TICKS = 6;

// ---- Idle wiggle ----
// Small vertical bob the bean does when stationary on a cube. Communicates
// "the game is alive and waiting for you."
const IDLE_WIGGLE_PERIOD = 60; // ticks per full cycle
const IDLE_WIGGLE_AMPLITUDE = 1.2;

// ---- Level-clear card ----
// How long the celebration card holds before paint resets + rotation
// kicks in.
const LEVEL_CARD_TICKS = 90;
const LEVEL_ROTATE_TICKS = 30;

// ---- Falls (step 2) ----
// When the bean hops off the pyramid edge it spirals down and off the
// screen, then the cabinet respawns on the apex (or game-over if lives
// = 0). Total fall duration:
const FALL_TICKS = 50;
// Vertical fall distance (the bean travels this far below its starting
// point before disappearing off-screen).
const FALL_DROP = 240;
// Horizontal drift while falling — positive for NE/SE direction, negative
// for NW/SW. Picked per-fall based on which edge we left.
const FALL_DRIFT = 30;
// Screen shake intensity + duration on the impact frame of a fall.
const SHAKE_TICKS = 14;
const SHAKE_AMPLITUDE = 4;
// Brief "respawn invuln" overlay (the bean blinks). No actual invuln
// matters yet in step 2 (no enemies); the blink is a "you just lost a
// life, you're back" cue for the player.
const RESPAWN_BLINK_TICKS = 30;
// Life-pip pulse — the right-gutter pips flash red when a life is lost.
const LIFE_PIP_PULSE_TICKS = 30;

// ---- Game over (step 2) ----
const STARTING_LIVES = 3;
// CRT-glitch effect on the game-over screen — the longer the player
// hangs out, the more the glitch intensifies. Capped so it doesn't
// fully obscure the score.
const GLITCH_INTRO_TICKS = 24;
// Ticks the player must wait before Z restarts — prevents accidentally
// blowing past the game-over card while mashing Z.
const GAME_OVER_INPUT_LOCK_TICKS = 30;
// Death-pause: how long the game-over CRT-glitch panel renders BEFORE
// we declare isGameOver() = true to the launcher. The launcher pauses
// the cartridge the tick after isGameOver() returns true and shows the
// initials overlay; this delay gives the player a beat to read "GAME
// OVER" before the score-submission UI scrims over.
const DEATH_PAUSE_TICKS = 36; // 0.6s at 60Hz

// ---- Snake (step 3) ----
// Pre-spawn delay base is per-level (see getLevelDifficulty). Jitter
// applies regardless so the player can't memorize the exact spawn
// frame across runs.
const SNAKE_PRE_SPAWN_JITTER = 30; // ±0.5s
// Cadence — ticks between snake hops. Slower than the player's max
// hop rate so a clear pyramid lets the player outrun the snake; close
// enough to be threatening when cornered.
const SNAKE_HOP_CADENCE = 45;
// Snake hop arc — shorter than the bean's so the snake reads as
// "shorter-bodied predator" not "another bean."
const SNAKE_HOP_ARC_TICKS = 14;
const SNAKE_HOP_ARC_HEIGHT = 12;
// Re-spawn grace after a player death (or game-over recovery).
const SNAKE_RESPAWN_GRACE = 180; // 3s
// DANGER! card duration on snake spawn.
const DANGER_CARD_TICKS = 60;
// Snake idle bob — same primitive as the bean's wiggle, slightly
// faster so the snake reads as "tense, watching."
const SNAKE_IDLE_PERIOD = 36;
const SNAKE_IDLE_AMPLITUDE = 1.5;
// Mini-text countdown bar width in the right gutter during pre-spawn.
const SNAKE_COUNTDOWN_BAR_W = 56;
const SNAKE_COUNTDOWN_BAR_H = 4;

// ---- Red balls (step 4) ----
// Spawn cadence base + cap are per-level (see getLevelDifficulty).
// Jitter applies on top so spawn timing isn't memorizable.
const BALL_SPAWN_JITTER = 36;    // ±0.6s
// Hop cadence — slower than the snake so the player can read multiple
// threats. Also gives the ball a "rolling" feel rather than predatory.
const BALL_HOP_CADENCE = 50;
const BALL_HOP_ARC_TICKS = 14;
const BALL_HOP_ARC_HEIGHT = 10;
// Pre-spawn warning flash — pulses at the apex BALL_WARNING_FLASH_TICKS
// before the ball materializes, so the player gets a beat to react.
const BALL_WARNING_FLASH_TICKS = 30;
// Ball size — slightly smaller than the bean so the silhouette reads
// as "obstacle" not "rival character."
const BALL_RADIUS = 6;
// Squash-on-land — the ball briefly flattens vertically when it
// completes a hop, then springs back. Reads as bouncy.
const BALL_SQUASH_TICKS = 5;

// ---- Zones + GET READY card (step 5) ----
// Level ranges per zone. ALPHA = launch palette, BETA = sapphire/gold
// shift starting at level 6. (Step 6 may add a third zone for level
// 10 = pyramid-10 win celebration territory.)
const BETA_ZONE_START = 6;
// GET READY card lifetime. Player sees "GET READY — PYRAMID N" with
// a 3-2-1 countdown before each level starts. Without this the next
// level felt like a hard cut after the rotation wipe; with it the
// player gets a beat to position mentally.
const GET_READY_TICKS = 120; // 2s at 60Hz
// Round-bonus scoring on level clear.
const ROUND_BONUS_PER_LIFE = 500;
const SPEED_BONUS_THRESHOLD_TICKS = 1800; // 30s
const SPEED_BONUS = 500;

// ---- TREASURE! win cinematic (step 6) ----
// Clearing pyramid WIN_PYRAMID triggers the win path: instead of
// rotating to pyramid 11, fire the TREASURE! cinematic and route to
// game-over with a "you completed the cartridge" message.
const WIN_PYRAMID = 10;
const WIN_BONUS = 10000;
// How long the TREASURE! card holds before flipping to game-over.
const WIN_CARD_TICKS = 240; // 4s


// ---- Attract-mode demo loop ----
// The cabinet starts the cartridge with the player NOT having pressed
// start yet (the cabinet runs a press-to-start gate on first round —
// see sc-2823). But once gameplay begins, if the player just idles
// indefinitely we want the cartridge to feel alive, so we run a soft
// scripted demo of the bean hopping around painting cubes. This is
// purely cosmetic — input from the player snaps back to real control
// instantly.
//
// In step 1 this triggers after IDLE_TO_ATTRACT_TICKS of zero input.
const IDLE_TO_ATTRACT_TICKS = 600; // 10s at 60Hz
// Scripted attract sequence. Each entry is a diagonal direction. Loops.
type Dir = 'NE' | 'NW' | 'SE' | 'SW';

/** Zone progression — what visual + difficulty band the player is in.
 *  Level 1-5 = ALPHA (launch palette), level 6-10 = BETA (sapphire +
 *  gold). The palette swap is a sensory "you've graduated to harder
 *  territory" signal; gameplay constants don't change yet (snake +
 *  ball cadence stay the same in step 5; step 6 may add per-zone
 *  difficulty knobs). */
type Zone = 'alpha' | 'beta';

function zoneForLevel(level: number): Zone {
  return level >= BETA_ZONE_START ? 'beta' : 'alpha';
}

/** Per-level difficulty knobs. Threats ramp with the level so the
 *  player learns the verb (dodge + paint) before the snake debuts,
 *  and the ball cap grows so the screen never goes from calm to
 *  swarm overnight.
 *
 *  Ramp shape (`getLevelDifficulty`):
 *    1-3: no snake, balls only (cap 1→2)
 *    4-5: snake debuts with long pre-spawn (7s → 5s)
 *    6+:  full pressure — ball cap 3, snake at 5s; level 7+ tightens
 *         ball spawn interval to 2.5s for a relentless feel
 */
interface LevelDifficulty {
  /** True if the snake should spawn this level. False = snake-free. */
  snakeEnabled: boolean;
  /** Ticks before the FIRST snake of the level spawns. Ignored if
   *  snakeEnabled is false. Re-spawn grace after death uses
   *  SNAKE_RESPAWN_GRACE regardless. */
  snakePreSpawnTicks: number;
  /** Max simultaneous balls on the pyramid. Capped to keep the screen
   *  readable; ramps from 1 (level 1) to 3 (level 6+). */
  ballCap: number;
  /** Ticks between ball spawns. Tightens at higher levels. */
  ballSpawnIntervalTicks: number;
}

function getLevelDifficulty(level: number): LevelDifficulty {
  if (level <= 1) {
    return {
      snakeEnabled: false,
      snakePreSpawnTicks: 0,
      ballCap: 1,
      ballSpawnIntervalTicks: 240, // 4s
    };
  }
  if (level === 2) {
    return {
      snakeEnabled: false,
      snakePreSpawnTicks: 0,
      ballCap: 1,
      ballSpawnIntervalTicks: 210, // 3.5s
    };
  }
  if (level === 3) {
    return {
      snakeEnabled: false,
      snakePreSpawnTicks: 0,
      ballCap: 2,
      ballSpawnIntervalTicks: 180, // 3s
    };
  }
  if (level === 4) {
    return {
      snakeEnabled: true,
      snakePreSpawnTicks: 420, // 7s — long grace as the snake debuts
      ballCap: 2,
      ballSpawnIntervalTicks: 180,
    };
  }
  if (level === 5) {
    return {
      snakeEnabled: true,
      snakePreSpawnTicks: 300, // 5s
      ballCap: 2,
      ballSpawnIntervalTicks: 180,
    };
  }
  if (level === 6) {
    return {
      snakeEnabled: true,
      snakePreSpawnTicks: 300,
      ballCap: 3,
      ballSpawnIntervalTicks: 180,
    };
  }
  // 7+ — full pressure with tightened ball cadence.
  return {
    snakeEnabled: true,
    snakePreSpawnTicks: 240, // 4s
    ballCap: 3,
    ballSpawnIntervalTicks: 150, // 2.5s
  };
}

/** Per-zone palette bundle. Picked from this on every render so the
 *  BETA shift is purely data-driven and adding more zones in the
 *  future is a one-token-per-zone edit instead of a render-path
 *  rewrite. */
interface ZonePalette {
  skyTop: string;
  skyBottom: string;
  gridLine: string;
  gridGlow: string;
  gutterBg: string;
  gutterEdge: string;
  cubeTopUnpainted: string;
  cubeTopPainted: string;
  cubeLeftUnpainted: string;
  cubeLeftPainted: string;
  cubeRightUnpainted: string;
  cubeRightPainted: string;
}
const ATTRACT_SCRIPT: readonly Dir[] = [
  'SE', 'SE', 'SE', 'SW', 'SW', 'SW',
  'NE', 'NE', 'NE', 'NW', 'NW', 'NW',
  'SE', 'NW', 'SW', 'NE',
];
const ATTRACT_STEP_TICKS = 24; // ticks between scripted hops

// ---- Score ----
const SCORE_PER_PAINT = 25;
const LEVEL_CLEAR_BONUS = 1000;

// ---- HUD ----
const HUD_TOP_H = 22;

/** A single cube on the pyramid. `painted` flips when the bean lands on
 *  the cube's top face for the first time this level. `flashTicks` is
 *  the remaining duration of the white paint-flash that fires on the
 *  paint frame. */
interface Cube {
  readonly row: number;
  readonly col: number;
  painted: boolean;
  flashTicks: number;
}

/** Phase of the cartridge's state machine.
 *  - playing: normal hop input + paint
 *  - falling: bean off the pyramid, animating down + off-screen
 *  - respawning: bean back on top cube, blinking
 *  - level-card: "PYRAMID CLEAR!" card showing
 *  - rotating: CRT scan-line wipe between levels
 *  - get-ready: "GET READY — PYRAMID N" card with 3-2-1 countdown
 *    (step 5). Sits between 'rotating' and 'playing'. On the boundary
 *    into BETA (level 6), the card also announces the zone change
 *    and the palette interpolates over the card's lifetime.
 *  - danger-card: "SNAKE INCOMING!" card on snake spawn (step 3)
 *  - death-pause: brief panel-only phase between the final fall and
 *    the cabinet's initials overlay. Lets the player SEE the CRT-glitch
 *    game-over panel render for ~0.6s before the launcher's
 *    isGameOver() polling triggers the initials prompt that scrims
 *    over the playfield. Without this, the player perceives a freeze
 *    mid-game with no explanation.
 *  - game-over: final card + CRT-glitch overlay; Z restarts after lock
 */
type Phase =
  | 'playing'
  | 'falling'
  | 'respawning'
  | 'level-card'
  | 'rotating'
  | 'get-ready'
  | 'danger-card'
  | 'death-pause'
  | 'treasure-card' // pyramid WIN_PYRAMID cleared — win cinematic (step 6)
  | 'game-over';

/** Snake enemy state.
 *
 *  The snake exists between spawn and player-death. It has its own hop
 *  arc (independent of the bean) and hops every SNAKE_HOP_CADENCE ticks.
 *  Chase AI: at each decision tick, pick the legal diagonal (one that
 *  keeps the snake on the pyramid) that minimizes Manhattan distance
 *  to the player's destination cube. Tiebreaks favor the snake's
 *  previous direction for visual continuity.
 */
interface Snake {
  row: number;
  col: number;
  // Mid-hop state — same shape as the bean's jump arc fields.
  hopTicks: number;
  hopFromRow: number;
  hopFromCol: number;
  // Cooldown until the next decision-tick when hopTicks reaches 0.
  // Counts down each tick; at 0 the snake picks its next move.
  cooldown: number;
  // Last direction taken. Used as a tiebreak in the AI so the snake
  // doesn't zig-zag erratically on equally-optimal moves.
  lastDir: Dir | null;
  // Idle bob phase — tick counter for the per-snake wiggle.
  bobTicks: number;
}

/** Red ball — falls down the pyramid one diagonal step per beat,
 *  despawns off the bottom edge. Unlike the snake, balls don't choose
 *  their direction based on the player — each hop's SE/SW pick is
 *  PRNG-driven so the player can't memorize a safe column.
 */
interface Ball {
  row: number;
  col: number;
  hopTicks: number;
  hopFromRow: number;
  hopFromCol: number;
  cooldown: number;
  // Squash-on-land animation tick counter. Counts down from
  // BALL_SQUASH_TICKS to 0 after each landing.
  squashTicks: number;
}

export class CubeJumpInstance implements GameInstance {
  private readonly ctx: GameContext;

  // Player state.
  private playerRow = 0;
  private playerCol = 0;
  // Jump animation. When jumpTicks > 0 the bean is mid-arc between the
  // FROM cube and TO cube. At jumpTicks = 0 the bean is fully on
  // playerRow/playerCol and we apply the paint.
  private jumpTicks = 0;
  private jumpFromRow = 0;
  private jumpFromCol = 0;

  // Pyramid state.
  private cubes: Cube[] = [];

  // Level state.
  private level = 1;
  private score = 0;
  private finalScore = 0;
  // Tick the current level started — used to compute the speed bonus
  // on level clear (cleared in < SPEED_BONUS_THRESHOLD_TICKS = +bonus).
  private levelStartedAtTick = 0;
  // Round-bonus accounting — last cleared level's round bonus, for
  // display on the PYRAMID CLEAR! card. Resets each level start.
  private lastRoundBonus = 0;
  private lastSpeedBonus = 0;
  // Phase + the tick countdown inside that phase.
  private phase: Phase = 'playing';
  private phaseTicks = 0;

  // Lives. Step 2 adds the actual life-loss path.
  private lives = STARTING_LIVES;
  // Index of the life pip currently pulsing red after a life-loss
  // (so the player sees which pip just extinguished). -1 = no pulse.
  private pulsingPipIndex = -1;
  private pipPulseTicks = 0;

  // Fall animation state. When phase = 'falling', this drives the
  // bean's screen position.
  private fallStartX = 0;
  private fallStartY = 0;
  private fallDriftDir: 1 | -1 = 1;
  // Screen-shake offset applied to the entire render. Counts down to 0
  // over SHAKE_TICKS, mixed with a high-frequency oscillation.
  private shakeTicks = 0;
  // Respawn blink — separate from phase so we can re-enter 'playing'
  // immediately (input is live) while the bean still flashes.
  private respawnBlinkTicks = 0;
  // Game-over input lock — Z (fireA) won't restart until this hits 0.
  private gameOverLockTicks = 0;
  // True once Z has been pressed AFTER the lock expires. The launcher
  // reads shouldRestart() and resets us.
  private restartArmed = false;

  // World tick — drives idle-wiggle, attract loop, grid shimmer.
  private worldTicks = 0;
  // Ticks since the player last gave input. When this exceeds
  // IDLE_TO_ATTRACT_TICKS we drive the scripted demo.
  private idleTicks = 0;
  // Position inside the attract script.
  private attractStep = 0;
  // Tick counter for the attract scripted hop cadence.
  private attractCooldown = 0;

  // Latched input edges. We read justPressed for the four diagonal
  // moves on each `input(state)` and act in `update`.
  private inputQueued: Dir | null = null;

  // ---- Snake state (step 3) ----
  // The snake is created the moment the DANGER! card finishes; null
  // means no snake is currently on the pyramid. The pre-spawn timer
  // ticks down from level-start AND after each respawn-grace; when it
  // hits 0 we fire the danger card and spawn.
  private snake: Snake | null = null;
  // Ticks until the next snake spawn — counts down regardless of
  // whether the snake exists (paused while snake is alive). Set on
  // level start + on respawn-grace.
  private snakePreSpawnTicks = 0;
  // True while the pre-spawn countdown is "armed" (HUD shows the
  // SNAKE INCOMING bar). False while a snake is alive on the pyramid.
  private snakeArmed = false;

  // ---- TREASURE! win (step 6) ----
  // True once the player clears pyramid 10. Renders the win cinematic
  // overlay instead of letting play continue past the final pyramid.
  private wonTheCartridge = false;

  // ---- Ball state (step 4) ----
  // Active balls on the pyramid. Capped per-level by
  // getLevelDifficulty(this.level).ballCap so the screen doesn't get
  // unreadable on early levels (cap 1) or under-stocked on late
  // ones (cap 3).
  private balls: Ball[] = [];
  // Ticks until the next ball spawn. We arm a fresh delay each time
  // a ball spawns AND on level start.
  private ballSpawnTicks = 0;
  // Pre-spawn warning state. When ballWarningTicks > 0 we render a
  // pulsing red glow at the spawn cube; when it hits 0 the ball
  // actually materializes.
  private ballWarningRow = 0;
  private ballWarningCol = 0;
  private ballWarningTicks = 0;

  constructor(ctx: GameContext) {
    this.ctx = ctx;
    this.cubes = buildPyramid();
    // Level 1 difficulty: no snake, 1 ball, 4s spawn cadence.
    const diff = getLevelDifficulty(this.level);
    this.armNextSnakeSpawn(diff.snakePreSpawnTicks);
    this.armNextBallSpawn(diff.ballSpawnIntervalTicks);
    // Level 1 starts at worldTicks 0 — stamp so the speed bonus
    // math has a baseline.
    this.levelStartedAtTick = 0;
  }

  // ---- Public surface (cabinet calls these) ----

  input(state: InputState): void {
    // Game-over: Z (fireA) arms a restart once the input lock expires.
    // The launcher polls shouldRestart() and restarts the round on
    // its own; we just flip the flag.
    if (this.phase === 'game-over') {
      if (
        this.gameOverLockTicks <= 0 &&
        state.justPressed.has('fireA')
      ) {
        this.restartArmed = true;
      }
      return;
    }

    // Diagonal input. We map the 4-direction d-pad to four iso
    // directions:
    //   up + right → NE
    //   up + left  → NW
    //   down + right → SE
    //   down + left  → SW
    //
    // Pure up or pure down with no horizontal isn't a legal move in
    // Q*bert — the player has to commit to a diagonal. We require
    // one axis to be `justPressed` (the fresh edge that triggers the
    // hop) and the other axis to be `held` (the direction the player
    // chose). That way "hold right, tap up" reads as NE without
    // forcing the player to release a key between diagonals.
    const upJP = state.justPressed.has('up');
    const downJP = state.justPressed.has('down');
    const leftJP = state.justPressed.has('left');
    const rightJP = state.justPressed.has('right');
    const upH = state.up;
    const downH = state.down;
    const leftH = state.left;
    const rightH = state.right;

    let dir: Dir | null = null;
    if (upJP && rightH) dir = 'NE';
    else if (upJP && leftH) dir = 'NW';
    else if (downJP && rightH) dir = 'SE';
    else if (downJP && leftH) dir = 'SW';
    else if (rightJP && upH) dir = 'NE';
    else if (rightJP && downH) dir = 'SE';
    else if (leftJP && upH) dir = 'NW';
    else if (leftJP && downH) dir = 'SW';

    if (dir !== null) {
      this.inputQueued = dir;
      this.idleTicks = 0;
    }
  }

  update(_dt: number): void {
    this.worldTicks++;
    // Screen shake decays every tick regardless of phase.
    if (this.shakeTicks > 0) this.shakeTicks--;
    if (this.pipPulseTicks > 0) this.pipPulseTicks--;
    if (this.respawnBlinkTicks > 0) this.respawnBlinkTicks--;

    // Snake pre-spawn countdown — runs ONLY during 'playing' phase.
    // Pause it during all other phases so the HUD timer doesn't tick
    // down while the player is mid-fall or watching the level-card.
    if (
      this.snakeArmed &&
      this.phase === 'playing' &&
      this.snakePreSpawnTicks > 0
    ) {
      this.snakePreSpawnTicks--;
      if (this.snakePreSpawnTicks <= 0) {
        // Fire the danger card; snake spawns the moment the card
        // dismisses.
        this.phase = 'danger-card';
        this.phaseTicks = DANGER_CARD_TICKS;
        this.ctx.audio.sfx('hiss');
      }
    }

    if (this.phase === 'danger-card') {
      this.phaseTicks--;
      if (this.phaseTicks <= 0) {
        this.spawnSnake();
        this.phase = 'playing';
      }
      return;
    }

    if (this.phase === 'level-card') {
      this.phaseTicks--;
      if (this.phaseTicks <= 0) {
        this.phase = 'rotating';
        this.phaseTicks = LEVEL_ROTATE_TICKS;
      }
      return;
    }

    if (this.phase === 'rotating') {
      this.phaseTicks--;
      if (this.phaseTicks <= 0) {
        // Rotation complete: bump level, reset paint, drop bean back on
        // top cube. The rotation is a real grid transform in later
        // steps (changes which corner cubes are safe edges); in step 2
        // with no falls FROM the rotation, the rotation is purely
        // visual. We bump `level` here so the HUD displays the new
        // value, then hand off to the GET READY card before play.
        this.level++;
        for (const c of this.cubes) c.painted = false;
        this.playerRow = 0;
        this.playerCol = 0;
        this.jumpTicks = 0;
        // New level — snake gone, balls cleared. Spawn arming
        // happens at the END of GET READY (not now) so the timers
        // don't tick down behind the card.
        this.snake = null;
        this.balls = [];
        this.ballWarningTicks = 0;
        this.phase = 'get-ready';
        this.phaseTicks = GET_READY_TICKS;
      }
      return;
    }

    if (this.phase === 'get-ready') {
      this.phaseTicks--;
      if (this.phaseTicks <= 0) {
        // Card dismissed — start the new level. Arm fresh threat
        // timers per the new level's difficulty (early levels skip
        // the snake entirely).
        const diff = getLevelDifficulty(this.level);
        this.armNextSnakeSpawn(diff.snakePreSpawnTicks);
        this.armNextBallSpawn(diff.ballSpawnIntervalTicks);
        this.levelStartedAtTick = this.worldTicks;
        this.phase = 'playing';
        this.phaseTicks = 0;
      }
      return;
    }

    if (this.phase === 'falling') {
      this.phaseTicks--;
      if (this.phaseTicks <= 0) {
        // Fall complete: lose a life, decide respawn vs game-over.
        this.lives--;
        this.pulsingPipIndex = this.lives;
        this.pipPulseTicks = LIFE_PIP_PULSE_TICKS;
        if (this.lives <= 0) {
          // Route through 'death-pause' so the cabinet's initials
          // overlay doesn't snap up the instant the bean lands its
          // fall — we want ~0.6s of CRT-glitch panel time first so
          // the player sees GAME OVER before the score-submission UI
          // scrims over.
          this.phase = 'death-pause';
          this.phaseTicks = DEATH_PAUSE_TICKS;
          this.gameOverLockTicks = GAME_OVER_INPUT_LOCK_TICKS;
          this.finalScore = this.score;
          // Snake + balls despawn now so they don't sit on the
          // pyramid visible behind the game-over panel.
          this.snake = null;
          this.balls = [];
          this.ballWarningTicks = 0;
          return;
        }
        // Respawn on the apex with a brief blink.
        this.playerRow = 0;
        this.playerCol = 0;
        this.jumpTicks = 0;
        this.shakeTicks = SHAKE_TICKS;
        this.respawnBlinkTicks = RESPAWN_BLINK_TICKS;
        // Snake despawns when the player dies. Re-arm a fresh spawn
        // countdown so the player gets a moment to breathe. Balls
        // clear too — fresh slate after each respawn so the player
        // isn't ambushed by the same wave that just killed them.
        // Use current level's difficulty for both: snake-disabled
        // levels stay snake-free, ball interval matches the level.
        this.snake = null;
        this.balls = [];
        this.ballWarningTicks = 0;
        const diff = getLevelDifficulty(this.level);
        this.armNextSnakeSpawn(SNAKE_RESPAWN_GRACE);
        this.armNextBallSpawn(diff.ballSpawnIntervalTicks);
        this.phase = 'playing';
      }
      return;
    }

    if (this.phase === 'treasure-card') {
      this.phaseTicks--;
      if (this.phaseTicks <= 0) {
        // Cartridge complete — route to game-over so the launcher
        // submits the score and shows the leaderboard. The win flag
        // gates a different game-over render (TREASURE! TRIUMPH vs.
        // bare GAME OVER).
        this.phase = 'game-over';
        this.phaseTicks = 0;
        this.gameOverLockTicks = GAME_OVER_INPUT_LOCK_TICKS;
        this.finalScore = this.score;
      }
      return;
    }

    if (this.phase === 'death-pause') {
      // Render the GAME OVER CRT-glitch panel for DEATH_PAUSE_TICKS
      // ticks before isGameOver() goes true. The renderer uses
      // phaseTicks to drive the glitch intro animation; we decrement
      // here so the renderer sees a smoothly increasing "intro
      // progress" as ticks fall from DEATH_PAUSE_TICKS to 0.
      this.phaseTicks--;
      if (this.phaseTicks <= 0) {
        // Switch to real game-over. The launcher's isGameOver() poll
        // will catch this on the next tick and trigger the initials
        // overlay.
        this.phase = 'game-over';
        this.phaseTicks = 0;
      }
      return;
    }

    if (this.phase === 'game-over') {
      if (this.gameOverLockTicks > 0) this.gameOverLockTicks--;
      // CRT-glitch intro animates over the first GLITCH_INTRO_TICKS.
      // After that the glitch is steady-state for as long as the
      // player lingers. phaseTicks counts UP so the renderer can drive
      // the animation off a single counter.
      this.phaseTicks++;
      return;
    }

    // phase === 'playing'

    // Mid-air bean: tick down the jump, land at 0.
    if (this.jumpTicks > 0) {
      this.jumpTicks--;
      if (this.jumpTicks === 0) {
        this.landOnCurrentCube();
      }
      // Player still mid-jump — ignore queued input until they land.
      // This is intentional. Q*bert's snappy feel comes from "one hop
      // commits"; queueing the next hop while airborne lets the player
      // chain too fast and undercuts the deliberation of each move.
      this.idleTicks++;
      return;
    }

    // Bean is on a cube. Read queued input.
    const queued = this.inputQueued;
    this.inputQueued = null;

    let dir: Dir | null = queued;

    // No real input — drive attract-mode demo if we've idled long enough.
    if (dir === null) {
      this.idleTicks++;
      if (this.idleTicks > IDLE_TO_ATTRACT_TICKS) {
        if (this.attractCooldown <= 0) {
          dir = ATTRACT_SCRIPT[this.attractStep % ATTRACT_SCRIPT.length] ?? null;
          this.attractStep++;
          this.attractCooldown = ATTRACT_STEP_TICKS;
        } else {
          this.attractCooldown--;
        }
      }
    } else {
      // Real input — reset attract progression so when the player
      // walks away again the demo restarts at the top of the script.
      this.attractStep = 0;
      this.attractCooldown = 0;
    }

    if (dir !== null) {
      this.tryHop(dir);
    }

    // Snake tick — runs every frame in 'playing'. Independent of the
    // bean's hop state so the snake keeps closing in even when the
    // player is mid-arc. Collision check happens INSIDE updateSnake
    // when the snake completes a hop AND when the bean lands above.
    this.updateSnake();
    // Ball tick — same pattern: balls hop on their own cadence,
    // collision is checked on both their landing AND the bean's.
    this.updateBalls();
  }

  render(g: CanvasRenderingContext2D): void {
    // Screen-shake offset. Applied as a canvas translate around the
    // entire frame so the HUD shakes with the playfield — the cabinet
    // is rattling, not just the pyramid.
    let shakeX = 0;
    let shakeY = 0;
    if (this.shakeTicks > 0) {
      const decay = this.shakeTicks / SHAKE_TICKS;
      // High-freq oscillation × decay envelope. Different phases on X
      // vs Y so the shake reads as a jolt, not a slide.
      shakeX = Math.sin(this.worldTicks * 2.7) * SHAKE_AMPLITUDE * decay;
      shakeY = Math.cos(this.worldTicks * 3.1) * SHAKE_AMPLITUDE * decay;
    }
    g.save();
    g.translate(shakeX, shakeY);

    // Zone palette. During the GET READY card on a zone boundary
    // (entering BETA at level 6) we interpolate between the previous
    // and next palette as the card holds — palette shift becomes
    // synchronized with the announcement.
    const palette = this.currentZonePalette();
    drawSky(g, palette);
    drawGridLines(g, this.worldTicks, palette);
    drawGutters(g, palette);
    drawHudTop(g, this.score, this.level, zoneForLevel(this.level));
    drawHudGutters(
      g,
      this.level,
      this.lives,
      this.pulsingPipIndex,
      this.pipPulseTicks,
      this.snakeArmed,
      this.snakePreSpawnTicks,
      // Bar denominator: the BASE pre-spawn for this level + jitter.
      // Without this, bumping difficulty (level 4 = 7s base, level 5+
      // = 5s base) would visually shift the "full bar" target and
      // the bar would look misleadingly drained at level start.
      getLevelDifficulty(this.level).snakePreSpawnTicks
    );

    // Pyramid drawn back-to-front (row 0 = back = top of pyramid).
    for (const cube of this.cubes) {
      drawCube(g, cube, palette);
    }

    // Bean. Picks one of three positions:
    //   - mid-jump arc between cubes
    //   - falling off the pyramid (drifts down + sideways past canvas)
    //   - idle on current cube (with bob)
    if (this.phase === 'falling') {
      const progress = 1 - this.phaseTicks / FALL_TICKS;
      // Quadratic vertical drop + small horizontal drift + spin.
      const px =
        this.fallStartX + this.fallDriftDir * FALL_DRIFT * progress;
      const py = this.fallStartY + FALL_DROP * progress * progress;
      const spin = progress * Math.PI * 3;
      drawBeanSpinning(g, px, py, spin);
    } else if (this.jumpTicks > 0) {
      const progress = 1 - this.jumpTicks / JUMP_ARC_TICKS;
      const from = cubeTopCenter(this.jumpFromRow, this.jumpFromCol);
      const to = cubeTopCenter(this.playerRow, this.playerCol);
      const px = from.x + (to.x - from.x) * progress;
      const py =
        from.y +
        (to.y - from.y) * progress -
        Math.sin(Math.PI * progress) * JUMP_ARC_HEIGHT;
      drawBean(g, px, py, 0);
    } else if (this.phase === 'playing' || this.phase === 'level-card') {
      // Respawn blink — bean is invisible on alternating frames during
      // the blink window.
      const blinkHide =
        this.respawnBlinkTicks > 0 &&
        Math.floor(this.respawnBlinkTicks / 4) % 2 === 1;
      if (!blinkHide) {
        const top = cubeTopCenter(this.playerRow, this.playerCol);
        const bob =
          Math.sin((this.worldTicks / IDLE_WIGGLE_PERIOD) * Math.PI * 2) *
          IDLE_WIGGLE_AMPLITUDE;
        drawBean(g, top.x, top.y, bob);
      }
    }

    // Ball spawn warning — pulsing glow at the warning cube while the
    // pre-spawn countdown ticks down. Drawn UNDER the snake/bean so a
    // collision still reads them on top.
    if (this.ballWarningTicks > 0) {
      this.drawBallWarning(g);
    }

    // Snake. Drawn after the bean so on overlap the snake reads on
    // top (matters during the kill frame; thereafter the snake is
    // gone and the bean is mid-fall off-screen anyway).
    if (this.snake !== null) {
      this.drawSnakeOnPyramid(g);
    }

    // Balls. Drawn on top of everything else on the pyramid so the
    // player can always see the threat. Iterate in spawn order so
    // the most-recent ball renders frontmost (which matches gravity
    // intuition: newer balls are higher up the pyramid → further back
    // in the iso depth order, BUT we want them visually frontmost so
    // the player tracks the freshest threat).
    for (const b of this.balls) {
      this.drawBallOnPyramid(g, b);
    }

    // Phase overlays.
    if (this.phase === 'level-card') {
      drawLevelCard(g, this.level, this.lastRoundBonus, this.lastSpeedBonus);
    } else if (this.phase === 'rotating') {
      const progress = 1 - this.phaseTicks / LEVEL_ROTATE_TICKS;
      drawRotateWipe(g, progress);
    } else if (this.phase === 'get-ready') {
      const elapsed = GET_READY_TICKS - this.phaseTicks;
      drawGetReadyCard(g, this.level, elapsed, this.isZoneTransition());
    } else if (this.phase === 'danger-card') {
      drawDangerCard(g, this.phaseTicks);
    } else if (this.phase === 'treasure-card') {
      drawTreasureCard(g, this.score, WIN_CARD_TICKS - this.phaseTicks);
    } else if (this.phase === 'death-pause') {
      // Same panel as 'game-over' but use the inverse tick count so
      // the CRT-glitch intro animates IN smoothly during the pause.
      // The Z prompt stays hidden until 'game-over' proper (we pass
      // a positive lock so drawGameOver renders "..." instead of
      // "PRESS Z").
      const intoTicks = DEATH_PAUSE_TICKS - this.phaseTicks;
      drawGameOver(
        g,
        this.finalScore,
        this.level,
        intoTicks,
        // Force "not yet" prompt state during death-pause.
        GAME_OVER_INPUT_LOCK_TICKS,
        // Death-pause is always the loss path — wonTheCartridge can
        // only be true after a treasure-card → game-over transition.
        false
      );
    } else if (this.phase === 'game-over') {
      drawGameOver(
        g,
        this.finalScore,
        this.level,
        // Add DEATH_PAUSE_TICKS so the glitch animation continues
        // smoothly from where death-pause left off (the panel was
        // already DEATH_PAUSE_TICKS into its intro when we entered
        // game-over). Without this addend, the glitch would reset
        // back to tick-0 intensity the instant phase flips.
        this.phaseTicks + DEATH_PAUSE_TICKS,
        this.gameOverLockTicks,
        this.wonTheCartridge
      );
    }

    // Attract banner — only when the demo is actually running and the
    // pyramid is otherwise idle.
    if (
      this.idleTicks > IDLE_TO_ATTRACT_TICKS &&
      this.phase === 'playing'
    ) {
      drawAttractBanner(g, this.worldTicks);
    }

    g.restore();
  }

  resize(_w: number, _h: number): void {
    // Cartridge renders to its declared 480×270; the cabinet's compositor
    // handles display scaling.
  }

  // Cabinet duck-typed convention surface.
  getScore(): number {
    return this.score;
  }
  getFinalScore(): number {
    return this.phase === 'game-over' ? this.finalScore : this.score;
  }
  isGameOver(): boolean {
    return this.phase === 'game-over';
  }
  shouldRestart(): boolean {
    return this.phase === 'game-over' && this.restartArmed;
  }
  pause(): void {
    // No long-lived audio voices to silence (BGM is deferred to a
    // cabinet-wide infrastructure story — see CUBE JUMP step 6 notes).
  }
  resume(): void {
    // Nothing to restore here.
  }
  teardown(): void {
    // No long-lived audio loops or resources. The cabinet's audio bus
    // owns the SFX recipes and survives cartridge teardown.
  }

  // ---- Private movement / paint logic ----

  /**
   * Attempt a hop in `dir`. On-pyramid moves commit the jump arc.
   * Off-pyramid moves trigger a fall — the bean spirals down past
   * the bottom of the canvas, then we lose a life and either respawn
   * or transition to game-over.
   */
  private tryHop(dir: Dir): void {
    const { dr, dc } = DIR_DELTA[dir];
    const tr = this.playerRow + dr;
    const tc = this.playerCol + dc;
    if (!isOnPyramid(tr, tc)) {
      // Off-pyramid: start a fall. The fall's screen-space origin is
      // the current cube's top-center; horizontal drift direction
      // follows the move direction so the bean visually exits the
      // edge it tried to leap toward.
      const from = cubeTopCenter(this.playerRow, this.playerCol);
      this.fallStartX = from.x;
      this.fallStartY = from.y;
      this.fallDriftDir = dc < 0 ? -1 : 1;
      this.phase = 'falling';
      this.phaseTicks = FALL_TICKS;
      this.ctx.audio.sfx('whoa');
      return;
    }
    this.jumpFromRow = this.playerRow;
    this.jumpFromCol = this.playerCol;
    this.playerRow = tr;
    this.playerCol = tc;
    this.jumpTicks = JUMP_ARC_TICKS;
    this.ctx.audio.sfx('hop');
  }

  /**
   * Bean has finished its jump arc and is now standing on
   * (playerRow, playerCol). Flip the cube's paint state, award score,
   * and check for level-clear.
   */
  private landOnCurrentCube(): void {
    // Enemy-on-this-tile check FIRST. If the bean lands on a snake
    // or a ball, die — no paint, no score. Check before paint so the
    // player doesn't get credit for a death-frame paint.
    if (this.snakeOnPlayerCube()) {
      this.killPlayerBySnake();
      return;
    }
    if (this.ballOnPlayerCube()) {
      this.killPlayerByBall();
      return;
    }

    const cube = this.cubeAt(this.playerRow, this.playerCol);
    if (cube && !cube.painted) {
      cube.painted = true;
      cube.flashTicks = PAINT_FLASH_TICKS;
      this.score += SCORE_PER_PAINT;
      this.ctx.audio.sfx('paint');
    }
    // Decay any in-flight flash on cubes other than the one we just
    // landed on — keeps the visual cadence clean.
    for (const c of this.cubes) {
      if (c !== cube && c.flashTicks > 0) c.flashTicks--;
    }
    // Level clear?
    if (this.cubes.every((c) => c.painted)) {
      this.score += LEVEL_CLEAR_BONUS;
      // Round bonuses: lives-remaining + speed bonus. Tracked for
      // display on the PYRAMID CLEAR! card.
      this.lastRoundBonus = this.lives * ROUND_BONUS_PER_LIFE;
      const clearedInTicks = this.worldTicks - this.levelStartedAtTick;
      this.lastSpeedBonus =
        clearedInTicks <= SPEED_BONUS_THRESHOLD_TICKS ? SPEED_BONUS : 0;
      this.score += this.lastRoundBonus + this.lastSpeedBonus;
      // Pyramid WIN_PYRAMID cleared → win cinematic instead of
      // rotating to pyramid 11. The player completed the cartridge.
      if (this.level >= WIN_PYRAMID) {
        this.score += WIN_BONUS;
        this.wonTheCartridge = true;
        this.snake = null;
        this.balls = [];
        this.ballWarningTicks = 0;
        this.phase = 'treasure-card';
        this.phaseTicks = WIN_CARD_TICKS;
        this.ctx.audio.sfx('level-clear');
        return;
      }
      this.phase = 'level-card';
      this.phaseTicks = LEVEL_CARD_TICKS;
      this.ctx.audio.sfx('level-clear');
    }
  }

  private cubeAt(row: number, col: number): Cube | undefined {
    return this.cubes.find((c) => c.row === row && c.col === col);
  }

  /**
   * Render the pre-spawn warning glow at the ball's incoming cube.
   * Pulses fast (every 4 ticks) so the player visually catches the
   * threat. Uses additive-blended circles to read as "glowing"
   * without an actual blend mode.
   */
  private drawBallWarning(g: CanvasRenderingContext2D): void {
    const { x, y } = cubeTopCenter(this.ballWarningRow, this.ballWarningCol);
    const pulse = Math.floor(this.ballWarningTicks / 4) % 2 === 0;
    const radius = pulse ? 10 : 7;
    // Outer glow.
    g.save();
    g.globalAlpha = 0.5;
    g.fillStyle = BALL_WARNING_GLOW;
    g.beginPath();
    g.ellipse(x, y, radius + 2, (radius + 2) * 0.5, 0, 0, Math.PI * 2);
    g.fill();
    // Inner pulse.
    g.globalAlpha = 0.85;
    g.fillStyle = BALL_WARNING;
    g.beginPath();
    g.ellipse(x, y, radius, radius * 0.5, 0, 0, Math.PI * 2);
    g.fill();
    g.restore();
  }

  /**
   * Render a single ball at its current screen position. Handles
   * mid-hop arc and the squash-on-land animation. Same iso-projection
   * helper as the bean + snake.
   */
  private drawBallOnPyramid(g: CanvasRenderingContext2D, b: Ball): void {
    let px: number;
    let py: number;
    if (b.hopTicks > 0) {
      const progress = 1 - b.hopTicks / BALL_HOP_ARC_TICKS;
      const from = cubeTopCenter(b.hopFromRow, b.hopFromCol);
      const to = cubeTopCenter(b.row, b.col);
      px = from.x + (to.x - from.x) * progress;
      py =
        from.y +
        (to.y - from.y) * progress -
        Math.sin(Math.PI * progress) * BALL_HOP_ARC_HEIGHT;
    } else {
      const top = cubeTopCenter(b.row, b.col);
      px = top.x;
      py = top.y;
    }
    // Squash: vertically compress + horizontally widen the ball for a
    // few ticks after landing.
    let rx = BALL_RADIUS;
    let ry = BALL_RADIUS;
    if (b.squashTicks > 0) {
      const t = b.squashTicks / BALL_SQUASH_TICKS;
      rx = BALL_RADIUS + 2 * t;
      ry = BALL_RADIUS - 2 * t;
    }
    // Bean's feet anchor — ball sits centered on the cube top with a
    // small lift so it doesn't intersect the diamond edge.
    const cy = py + BEAN_FOOT_OFFSET_Y - ry;
    // Shadow puddle on the cube directly underneath.
    g.fillStyle = 'rgba(0, 0, 0, 0.35)';
    g.beginPath();
    g.ellipse(px, py - 1, rx, 2, 0, 0, Math.PI * 2);
    g.fill();
    // Outline.
    g.fillStyle = BALL_DARK;
    g.beginPath();
    g.ellipse(px, cy, rx + 1, ry + 1, 0, 0, Math.PI * 2);
    g.fill();
    // Body.
    g.fillStyle = BALL_BODY;
    g.beginPath();
    g.ellipse(px, cy, rx, ry, 0, 0, Math.PI * 2);
    g.fill();
    // Highlight — small white dot upper-left for "rolling" feel.
    g.fillStyle = BALL_HIGHLIGHT;
    g.beginPath();
    g.ellipse(px - rx / 3, cy - ry / 3, rx / 3, ry / 3, 0, 0, Math.PI * 2);
    g.fill();
  }

  /**
   * Render the snake at its current screen position. Handles mid-hop
   * arc (interpolating between from-cube and to-cube with a parabolic
   * lift) and stationary (idle bob).
   */
  private drawSnakeOnPyramid(g: CanvasRenderingContext2D): void {
    const s = this.snake;
    if (s === null) return;
    let px: number;
    let py: number;
    if (s.hopTicks > 0) {
      const progress = 1 - s.hopTicks / SNAKE_HOP_ARC_TICKS;
      const from = cubeTopCenter(s.hopFromRow, s.hopFromCol);
      const to = cubeTopCenter(s.row, s.col);
      px = from.x + (to.x - from.x) * progress;
      py =
        from.y +
        (to.y - from.y) * progress -
        Math.sin(Math.PI * progress) * SNAKE_HOP_ARC_HEIGHT;
    } else {
      const top = cubeTopCenter(s.row, s.col);
      const bob =
        Math.sin((s.bobTicks / SNAKE_IDLE_PERIOD) * Math.PI * 2) *
        SNAKE_IDLE_AMPLITUDE;
      px = top.x;
      py = top.y + bob;
    }
    drawSnake(g, px, py, s.lastDir, s.bobTicks);
  }

  // ---- Zone helpers (step 5) ----

  /**
   * Build the palette in effect for the current frame. During the
   * GET READY card on a zone-boundary level, we interpolate between
   * the outgoing and incoming palettes so the shift is felt as a
   * fade, not a hard cut. The interpolation happens visually via two
   * draw passes (we draw the outgoing palette under a fading scrim
   * of the incoming palette). For simplicity in step 5 we just hard-
   * swap at the GET READY card midpoint — clean enough that a future
   * polish pass can replace with proper interpolation.
   */
  private currentZonePalette(): ZonePalette {
    // If we're in the GET READY card AND this is the BETA boundary,
    // swap to BETA halfway through the card so the announcement +
    // palette shift land together. Otherwise zone follows the level.
    let zone = zoneForLevel(this.level);
    if (this.phase === 'get-ready' && this.isZoneTransition()) {
      const elapsed = GET_READY_TICKS - this.phaseTicks;
      // First half of the card: outgoing zone (alpha). Second half:
      // incoming zone (beta).
      zone = elapsed < GET_READY_TICKS / 2 ? 'alpha' : 'beta';
    }
    return ZONE_PALETTES[zone];
  }

  /**
   * True if the level we just entered (during the current GET READY)
   * is the BETA boundary — i.e., level 6. Used to thread the "ZONE:
   * PYRAMID BETA" sub-announcement and the palette interpolation.
   */
  private isZoneTransition(): boolean {
    return this.level === BETA_ZONE_START;
  }

  // ---- Snake helpers (step 3) ----

  /**
   * Arm the next snake spawn with a jittered delay. We add ±jitter
   * ticks of PRNG-sourced jitter so the spawn isn't memorizable
   * frame-by-frame.
   *
   * If the current level's difficulty disables the snake (levels 1-3
   * per the ramp), this is a no-op — `snakeArmed` stays false so the
   * countdown bar and pre-spawn check both skip. PRNG draw still
   * happens so determinism across difficulty branches is preserved
   * (a snake-free level consumes the same number of PRNG values as
   * one with a snake; replay can be re-played regardless of which
   * branch the rule table takes).
   */
  private armNextSnakeSpawn(baseTicks: number): void {
    const jitter = this.ctx.random.int(
      -SNAKE_PRE_SPAWN_JITTER,
      SNAKE_PRE_SPAWN_JITTER
    );
    const difficulty = getLevelDifficulty(this.level);
    if (!difficulty.snakeEnabled) {
      this.snakePreSpawnTicks = 0;
      this.snakeArmed = false;
      return;
    }
    this.snakePreSpawnTicks = Math.max(60, baseTicks + jitter);
    this.snakeArmed = true;
  }

  /**
   * Materialize the snake. Spawns one row below the apex (row 1) on
   * either col 0 or col 1, chosen at random — both options are
   * adjacent to the apex but force a different opening move from the
   * player.
   */
  private spawnSnake(): void {
    const col = this.ctx.random.int(0, 1);
    this.snake = {
      row: 1,
      col,
      hopTicks: 0,
      hopFromRow: 1,
      hopFromCol: col,
      cooldown: SNAKE_HOP_CADENCE,
      lastDir: null,
      bobTicks: 0,
    };
    this.snakeArmed = false;
    // Immediate collision check — if the player is sitting on the
    // snake's spawn cube, that's a death the moment the snake lands.
    if (this.snakeOnPlayerCube()) {
      this.killPlayerBySnake();
    }
  }

  /**
   * One frame of snake simulation. Advances the hop arc; on landing,
   * checks collision; on cooldown expiry, picks the next chase move.
   */
  private updateSnake(): void {
    const s = this.snake;
    if (s === null) return;
    s.bobTicks++;

    // Mid-hop — tick the arc; on land, check collision + start cooldown.
    if (s.hopTicks > 0) {
      s.hopTicks--;
      if (s.hopTicks === 0) {
        if (this.snakeOnPlayerCube()) {
          this.killPlayerBySnake();
          return;
        }
        s.cooldown = SNAKE_HOP_CADENCE;
      }
      return;
    }

    // On the ground — count down cooldown, then pick next move.
    if (s.cooldown > 0) {
      s.cooldown--;
      return;
    }

    const dir = this.pickSnakeChaseDir(s);
    if (dir === null) {
      // No legal move — sit tight. (Shouldn't happen on a 7-row
      // pyramid; the snake always has at least one valid diagonal
      // unless it's at the bottom-corner of the pyramid AND the
      // top-side directions both lead to occupied cells, which
      // doesn't apply with a single snake.)
      s.cooldown = SNAKE_HOP_CADENCE;
      return;
    }
    const { dr, dc } = DIR_DELTA[dir];
    s.hopFromRow = s.row;
    s.hopFromCol = s.col;
    s.row += dr;
    s.col += dc;
    s.hopTicks = SNAKE_HOP_ARC_TICKS;
    s.lastDir = dir;
    this.ctx.audio.sfx('snake-hop');
  }

  /**
   * Snake AI: pick the legal diagonal that minimizes Manhattan
   * distance to the player's *destination* cube. Ties broken by
   * preserving the snake's last direction for visual continuity. Off-
   * pyramid moves are filtered out — the snake never self-falls.
   */
  private pickSnakeChaseDir(s: Snake): Dir | null {
    // Target the player's destination cube if they're mid-hop —
    // that's the Q*bert "predator anticipates you" feel. Otherwise
    // target their current cube.
    const targetRow = this.jumpTicks > 0 ? this.playerRow : this.playerRow;
    const targetCol = this.jumpTicks > 0 ? this.playerCol : this.playerCol;
    // Note: playerRow/playerCol always reflect the *destination* when
    // mid-hop (we update it in tryHop before starting the arc), so the
    // ternary above is functionally a no-op. Kept for clarity that
    // "we want destination cube" is the intent.

    let bestDir: Dir | null = null;
    let bestDist = Number.POSITIVE_INFINITY;
    // Iterate dirs in a fixed order so the AI is deterministic.
    const ORDER: readonly Dir[] = ['SE', 'SW', 'NE', 'NW'];
    for (const dir of ORDER) {
      const { dr, dc } = DIR_DELTA[dir];
      const nr = s.row + dr;
      const nc = s.col + dc;
      if (!isOnPyramid(nr, nc)) continue;
      const dist = Math.abs(nr - targetRow) + Math.abs(nc - targetCol);
      // Prefer last-dir as tiebreak so the snake doesn't zig-zag on
      // equal options — adds visual continuity.
      const tiebreak = dir === s.lastDir ? -0.5 : 0;
      const score = dist + tiebreak;
      if (score < bestDist) {
        bestDist = score;
        bestDir = dir;
      }
    }
    return bestDir;
  }

  /**
   * True if the snake and bean occupy the same cube AND the bean is
   * not mid-hop. We don't count "mid-air bean overlaps snake's cube"
   * as a kill — the bean has to actually land for the collision to
   * count, which matches Q*bert (Coily can't grab you while you're
   * jumping over him).
   */
  private snakeOnPlayerCube(): boolean {
    const s = this.snake;
    if (s === null) return false;
    if (this.jumpTicks > 0) return false;
    if (s.hopTicks > 0) return false;
    return s.row === this.playerRow && s.col === this.playerCol;
  }

  /**
   * Snake bite kill. Reuses the fall pipeline (spinning bean drops off
   * the pyramid) with the chomp SFX layered on. The fall handler
   * already covers life-decrement, respawn, and game-over.
   */
  private killPlayerBySnake(): void {
    const from = cubeTopCenter(this.playerRow, this.playerCol);
    this.fallStartX = from.x;
    this.fallStartY = from.y;
    // Drift away from the snake's last-known direction so the bean
    // visually flies "knocked off" by the snake.
    this.fallDriftDir = this.snake && this.snake.col >= this.playerCol ? -1 : 1;
    this.phase = 'falling';
    this.phaseTicks = FALL_TICKS;
    this.ctx.audio.sfx('chomp');
    // Snake despawns instantly so it doesn't follow the falling bean
    // off-pyramid in the render.
    this.snake = null;
  }

  // ---- Ball helpers (step 4) ----

  /**
   * Arm the next ball spawn with a jittered delay. PRNG draw for the
   * jitter so the spawn cadence isn't memorizable.
   */
  private armNextBallSpawn(baseTicks: number): void {
    const jitter = this.ctx.random.int(
      -BALL_SPAWN_JITTER,
      BALL_SPAWN_JITTER
    );
    this.ballSpawnTicks = Math.max(60, baseTicks + jitter);
  }

  /**
   * Ball simulation tick. Three phases per call:
   *   1. Spawn timer — if armed and we're under the level's ball cap,
   *      count down. When the timer fires, start a pre-spawn warning
   *      at the apex cube (the warning is purely visual; the ball
   *      materializes when the warning ticks down).
   *   2. Warning tick — if warning is active, count down. At 0,
   *      materialize the ball at the warning cube.
   *   3. Per-ball motion — advance hop arcs; on land, check collision
   *      with the bean; on cooldown expiry, pick next SE/SW; balls
   *      that would leave the pyramid (off the bottom edge) despawn.
   */
  private updateBalls(): void {
    const diff = getLevelDifficulty(this.level);
    // 1 — spawn timer
    if (
      this.balls.length < diff.ballCap &&
      this.ballWarningTicks <= 0 &&
      this.ballSpawnTicks > 0
    ) {
      this.ballSpawnTicks--;
      if (this.ballSpawnTicks <= 0) {
        // Pick a spawn cube — apex (0,0) or one of the row-1 cubes
        // ((1,0) or (1,1)). Three options, PRNG-picked, so the
        // player can't camp one column safely.
        const pick = this.ctx.random.int(0, 2);
        if (pick === 0) {
          this.ballWarningRow = 0;
          this.ballWarningCol = 0;
        } else if (pick === 1) {
          this.ballWarningRow = 1;
          this.ballWarningCol = 0;
        } else {
          this.ballWarningRow = 1;
          this.ballWarningCol = 1;
        }
        this.ballWarningTicks = BALL_WARNING_FLASH_TICKS;
        this.ctx.audio.sfx('ball-warning');
      }
    }

    // 2 — warning countdown → materialize
    if (this.ballWarningTicks > 0) {
      this.ballWarningTicks--;
      if (this.ballWarningTicks <= 0 && this.balls.length < diff.ballCap) {
        this.balls.push({
          row: this.ballWarningRow,
          col: this.ballWarningCol,
          hopTicks: 0,
          hopFromRow: this.ballWarningRow,
          hopFromCol: this.ballWarningCol,
          cooldown: BALL_HOP_CADENCE,
          squashTicks: 0,
        });
        // Re-arm the next spawn delay so the next ball is on a fresh
        // jittered clock using the current level's interval.
        this.armNextBallSpawn(diff.ballSpawnIntervalTicks);
        // Immediate collision check — if the player is on the spawn
        // cube, that's a kill the moment the ball lands.
        if (this.ballOnPlayerCube()) {
          this.killPlayerByBall();
          return;
        }
      }
    }

    // 3 — per-ball motion. Iterate by index because we may splice
    // off-pyramid balls mid-iteration; copy + filter at the end is
    // simpler and avoids index bugs.
    const survivors: Ball[] = [];
    for (const b of this.balls) {
      // Decay squash.
      if (b.squashTicks > 0) b.squashTicks--;

      // Mid-hop — advance arc; on land, check collision + arm cooldown.
      if (b.hopTicks > 0) {
        b.hopTicks--;
        if (b.hopTicks === 0) {
          b.squashTicks = BALL_SQUASH_TICKS;
          if (this.ballHitsPlayer(b)) {
            this.killPlayerByBall();
            return;
          }
          b.cooldown = BALL_HOP_CADENCE;
        }
        survivors.push(b);
        continue;
      }

      // On the ground — count down cooldown.
      if (b.cooldown > 0) {
        b.cooldown--;
        survivors.push(b);
        continue;
      }

      // Pick next direction — balls only go DOWN, SE or SW, 50/50.
      const goSE = this.ctx.random.int(0, 1) === 0;
      const dir: Dir = goSE ? 'SE' : 'SW';
      const { dr, dc } = DIR_DELTA[dir];
      const nr = b.row + dr;
      const nc = b.col + dc;
      if (!isOnPyramid(nr, nc)) {
        // Off the bottom edge — despawn. Don't push to survivors.
        // The next spawn was already armed when this ball materialized;
        // no extra bookkeeping needed.
        continue;
      }
      b.hopFromRow = b.row;
      b.hopFromCol = b.col;
      b.row = nr;
      b.col = nc;
      b.hopTicks = BALL_HOP_ARC_TICKS;
      this.ctx.audio.sfx('ball-bounce');
      survivors.push(b);
    }
    this.balls = survivors;
  }

  /**
   * True if `ball` and the bean occupy the same cube AND neither is
   * mid-hop. Same convention as snakeOnPlayerCube — mid-air bean
   * overlapping a ball's cube does not count as a kill.
   */
  private ballHitsPlayer(ball: Ball): boolean {
    if (this.jumpTicks > 0) return false;
    if (ball.hopTicks > 0) return false;
    return ball.row === this.playerRow && ball.col === this.playerCol;
  }

  /**
   * True if any ball overlaps the bean's current cube. Used when the
   * bean itself lands — we walk all balls to find any colocated one.
   */
  private ballOnPlayerCube(): boolean {
    if (this.jumpTicks > 0) return false;
    for (const b of this.balls) {
      if (b.hopTicks > 0) continue;
      if (b.row === this.playerRow && b.col === this.playerCol) return true;
    }
    return false;
  }

  /**
   * Ball-collision kill. Reuses the fall pipeline — distinct SFX
   * (`'ball-hit'`) so the player can tell ball-death from snake-death
   * by ear alone.
   */
  private killPlayerByBall(): void {
    const from = cubeTopCenter(this.playerRow, this.playerCol);
    this.fallStartX = from.x;
    this.fallStartY = from.y;
    // Drift sideways — pick the side opposite the bean's current col
    // so the knockback reads as "the ball shoved me off."
    this.fallDriftDir = this.playerCol < this.playerRow / 2 ? -1 : 1;
    this.phase = 'falling';
    this.phaseTicks = FALL_TICKS;
    this.ctx.audio.sfx('ball-hit');
    // All balls despawn — the bean's mid-fall, no need to keep
    // simulating threats that can't reach it.
    this.balls = [];
    this.ballWarningTicks = 0;
  }

}

// ---- Pyramid construction ----

function buildPyramid(): Cube[] {
  const cubes: Cube[] = [];
  for (let r = 0; r < PYRAMID_ROWS; r++) {
    for (let c = 0; c <= r; c++) {
      cubes.push({ row: r, col: c, painted: false, flashTicks: 0 });
    }
  }
  return cubes;
}

function isOnPyramid(row: number, col: number): boolean {
  if (row < 0 || row >= PYRAMID_ROWS) return false;
  if (col < 0 || col > row) return false;
  return true;
}

const DIR_DELTA: Record<Dir, { dr: number; dc: number }> = {
  NE: { dr: -1, dc: 0 },
  NW: { dr: -1, dc: -1 },
  SE: { dr: 1, dc: 1 },
  SW: { dr: 1, dc: 0 },
};

// ---- Iso projection ----

/**
 * Top-center pixel coordinate of the top diamond of cube (row, col).
 * This is the point the bean's feet should rest above.
 */
function cubeTopCenter(row: number, col: number): { x: number; y: number } {
  // Each cube top is a diamond CUBE_W wide × CUBE_H × 2 tall. Adjacent
  // cubes in the same row are offset by COL_DX horizontally. Each row
  // down is offset by ROW_DY vertically; the row also shifts left by
  // (row * COL_DX / 2) so the apex is centered above the bottom row.
  const x = PYRAMID_APEX_X + (col - row / 2) * COL_DX;
  const y = PYRAMID_APEX_Y + row * ROW_DY;
  return { x, y };
}

// ---- Rendering ----

/** Per-zone palette bundles. The renderer pulls colors from these
 *  instead of importing the raw tokens directly, so adding a new
 *  zone is just an entry here + a few token constants in palette.ts. */
const ZONE_PALETTES: Record<Zone, ZonePalette> = {
  alpha: {
    skyTop: SKY_TOP,
    skyBottom: SKY_BOTTOM,
    gridLine: GRID_LINE,
    gridGlow: GRID_GLOW,
    gutterBg: GUTTER_BG,
    gutterEdge: GUTTER_EDGE,
    cubeTopUnpainted: CUBE_TOP_UNPAINTED,
    cubeTopPainted: CUBE_TOP_PAINTED,
    cubeLeftUnpainted: CUBE_LEFT_UNPAINTED,
    cubeLeftPainted: CUBE_LEFT_PAINTED,
    cubeRightUnpainted: CUBE_RIGHT_UNPAINTED,
    cubeRightPainted: CUBE_RIGHT_PAINTED,
  },
  beta: {
    skyTop: BETA_SKY_TOP,
    skyBottom: BETA_SKY_BOTTOM,
    gridLine: BETA_GRID_LINE,
    gridGlow: BETA_GRID_GLOW,
    gutterBg: BETA_GUTTER_BG,
    gutterEdge: BETA_GUTTER_EDGE,
    cubeTopUnpainted: BETA_CUBE_TOP_UNPAINTED,
    cubeTopPainted: BETA_CUBE_TOP_PAINTED,
    cubeLeftUnpainted: BETA_CUBE_LEFT_UNPAINTED,
    cubeLeftPainted: BETA_CUBE_LEFT_PAINTED,
    cubeRightUnpainted: BETA_CUBE_RIGHT_UNPAINTED,
    cubeRightPainted: BETA_CUBE_RIGHT_PAINTED,
  },
};

function drawSky(g: CanvasRenderingContext2D, palette: ZonePalette): void {
  const grad = g.createLinearGradient(0, 0, 0, HEIGHT);
  grad.addColorStop(0, palette.skyTop);
  grad.addColorStop(1, palette.skyBottom);
  g.fillStyle = grad;
  g.fillRect(0, 0, WIDTH, HEIGHT);
}

function drawGridLines(
  g: CanvasRenderingContext2D,
  worldTicks: number,
  palette: ZonePalette
): void {
  // Faint iso grid lines behind the pyramid for "tile floating in
  // cyberspace" feel. The intersection glow pulses on a slow sine.
  g.save();
  g.strokeStyle = palette.gridLine;
  g.lineWidth = 1;
  g.globalAlpha = 0.5;
  const STEP = 28;
  // Vertical-ish iso lines (NW→SE).
  for (let i = -8; i < 24; i++) {
    const x0 = i * STEP;
    g.beginPath();
    g.moveTo(x0, 0);
    g.lineTo(x0 + HEIGHT, HEIGHT);
    g.stroke();
  }
  // Anti-diagonal (NE→SW).
  for (let i = -8; i < 24; i++) {
    const x0 = i * STEP;
    g.beginPath();
    g.moveTo(x0, 0);
    g.lineTo(x0 - HEIGHT, HEIGHT);
    g.stroke();
  }
  // Slow pulsing glow dots at a small lattice of intersections.
  const pulse =
    0.4 + 0.4 * Math.sin((worldTicks / 90) * Math.PI * 2);
  g.fillStyle = palette.gridGlow;
  g.globalAlpha = pulse;
  for (let y = 30; y < HEIGHT; y += 56) {
    for (let x = 30; x < WIDTH; x += 56) {
      g.fillRect(x, y, 2, 2);
    }
  }
  g.restore();
}

function drawGutters(g: CanvasRenderingContext2D, palette: ZonePalette): void {
  g.fillStyle = palette.gutterBg;
  g.fillRect(0, HUD_TOP_H, GUTTER_W, HEIGHT - HUD_TOP_H);
  g.fillRect(WIDTH - GUTTER_W, HUD_TOP_H, GUTTER_W, HEIGHT - HUD_TOP_H);
  // Inner edge — a thin lip suggests the gutters are panels in front of
  // the sky, not just colored backgrounds.
  g.fillStyle = palette.gutterEdge;
  g.fillRect(GUTTER_W - 1, HUD_TOP_H, 1, HEIGHT - HUD_TOP_H);
  g.fillRect(WIDTH - GUTTER_W, HUD_TOP_H, 1, HEIGHT - HUD_TOP_H);
}

function drawHudTop(
  g: CanvasRenderingContext2D,
  score: number,
  level: number,
  zone: Zone
): void {
  g.fillStyle = CARD_BG;
  g.fillRect(0, 0, WIDTH, HUD_TOP_H);
  g.fillStyle = GUTTER_EDGE;
  g.fillRect(0, HUD_TOP_H - 1, WIDTH, 1);

  g.font = '8px monospace';
  g.textBaseline = 'middle';

  // Score (left side of top bar).
  g.fillStyle = HUD_LABEL;
  g.fillText('SCORE', 8, HUD_TOP_H / 2 - 4);
  g.fillStyle = HUD_SCORE;
  g.font = '12px monospace';
  g.fillText(String(score).padStart(6, '0'), 8, HUD_TOP_H / 2 + 5);

  // Level (right side of top bar).
  g.font = '8px monospace';
  g.fillStyle = HUD_LABEL;
  g.fillText('PYRAMID', WIDTH - 50, HUD_TOP_H / 2 - 4);
  g.fillStyle = HUD_LEVEL;
  g.font = '12px monospace';
  g.fillText(String(level).padStart(2, '0'), WIDTH - 26, HUD_TOP_H / 2 + 5);

  // Zone badge — small Greek letter to the LEFT of the PYRAMID label.
  // Alpha = cyan α (current palette), beta = gold β. A subtle but
  // persistent cue that the player has crossed the BETA boundary.
  const badge = zone === 'beta' ? 'β' : 'α';
  const badgeColor = zone === 'beta' ? BETA_CUBE_TOP_PAINTED : CARD_BORDER;
  g.font = '10px monospace';
  g.fillStyle = badgeColor;
  g.fillText(badge, WIDTH - 60, HUD_TOP_H / 2 + 1);
}

function drawHudGutters(
  g: CanvasRenderingContext2D,
  _level: number,
  lives: number,
  pulsingPipIndex: number,
  pipPulseTicks: number,
  snakeArmed: boolean,
  snakePreSpawnTicks: number,
  snakePreSpawnBase: number
): void {
  // Left gutter: vertical "ARCADIA / CUBE JUMP" wordmark.
  g.save();
  g.translate(GUTTER_W / 2, HEIGHT / 2);
  g.rotate(-Math.PI / 2);
  g.font = '10px monospace';
  g.textBaseline = 'middle';
  g.textAlign = 'center';
  g.fillStyle = HUD_LABEL;
  g.fillText('CUBE JUMP', 0, -6);
  g.font = '6px monospace';
  g.fillStyle = ATTRACT_DIM;
  g.fillText('ARCADIA AMUSEMENTS', 0, 4);
  g.restore();

  // Right gutter: LIVES label + 3 pips. Each pip is either lit (a
  // life the player still has) or dim (already spent). The pip that
  // matches `pulsingPipIndex` flashes red on the fall-impact tick so
  // the player sees which life just went away.
  g.font = '8px monospace';
  g.textBaseline = 'middle';
  g.textAlign = 'center';
  g.fillStyle = HUD_LABEL;
  g.fillText('LIVES', WIDTH - GUTTER_W / 2, HEIGHT / 2 - 10);
  for (let i = 0; i < STARTING_LIVES; i++) {
    const x = WIDTH - GUTTER_W / 2 + (i - 1) * 12;
    const y = HEIGHT / 2 + 6;
    const lit = i < lives;
    const pulsing =
      i === pulsingPipIndex &&
      pipPulseTicks > 0 &&
      Math.floor(pipPulseTicks / 4) % 2 === 0;
    drawBeanPip(g, x, y, lit, pulsing);
  }

  // SNAKE INCOMING countdown — shows during the pre-spawn arm window.
  // A small magenta bar drains left-to-right as the spawn approaches.
  if (snakeArmed && snakePreSpawnTicks > 0) {
    const cx = WIDTH - GUTTER_W / 2;
    const labelY = HEIGHT / 2 + 36;
    g.font = '6px monospace';
    g.fillStyle = SNAKE_BODY;
    g.textAlign = 'center';
    g.fillText('SNAKE INCOMING', cx, labelY);
    // Bar — full = full delay, empty = spawn imminent. Denominator
    // is the level's BASE pre-spawn + jitter (varies by difficulty).
    const baseDelay = snakePreSpawnBase + SNAKE_PRE_SPAWN_JITTER;
    const fillRatio = Math.min(1, snakePreSpawnTicks / baseDelay);
    const barX = cx - SNAKE_COUNTDOWN_BAR_W / 2;
    const barY = labelY + 6;
    g.fillStyle = SNAKE_DARK;
    g.fillRect(barX, barY, SNAKE_COUNTDOWN_BAR_W, SNAKE_COUNTDOWN_BAR_H);
    g.fillStyle = SNAKE_BODY;
    g.fillRect(
      barX,
      barY,
      Math.floor(SNAKE_COUNTDOWN_BAR_W * fillRatio),
      SNAKE_COUNTDOWN_BAR_H
    );
  }

  g.textAlign = 'left';
}

function drawBeanPip(
  g: CanvasRenderingContext2D,
  x: number,
  y: number,
  lit: boolean,
  pulsing: boolean
): void {
  // Mini bean — 8×9. Lit = orange, dim = espresso-violet outline only
  // (so the slot is still visible but reads as "spent"), pulsing = the
  // attention-grabbing red flash on the frame a life is lost.
  let body: string;
  let outline = BEAN_OUTLINE;
  if (pulsing) {
    body = '#ff3030'; // blood red, matches the kill-you palette family
    outline = '#ffffff';
  } else if (lit) {
    body = HUD_LIVES;
  } else {
    body = ATTRACT_DIM;
    outline = ATTRACT_DIM;
  }
  g.fillStyle = body;
  g.fillRect(x - 3, y - 4, 6, 8);
  if (lit || pulsing) {
    g.fillStyle = BEAN_CREASE;
    g.fillRect(x, y - 4, 1, 8);
  }
  g.fillStyle = outline;
  g.fillRect(x - 4, y - 4, 1, 8);
  g.fillRect(x + 3, y - 4, 1, 8);
  g.fillRect(x - 3, y - 5, 6, 1);
  g.fillRect(x - 3, y + 4, 6, 1);
}

function drawCube(
  g: CanvasRenderingContext2D,
  cube: Cube,
  palette: ZonePalette
): void {
  const { x, y } = cubeTopCenter(cube.row, cube.col);
  const w = CUBE_W;
  const h = CUBE_H;
  const d = CUBE_DEPTH;

  const top = cube.painted ? palette.cubeTopPainted : palette.cubeTopUnpainted;
  const left = cube.painted ? palette.cubeLeftPainted : palette.cubeLeftUnpainted;
  const right = cube.painted ? palette.cubeRightPainted : palette.cubeRightUnpainted;
  const flashing = cube.flashTicks > 0;
  if (flashing) cube.flashTicks--;

  // Left face.
  g.fillStyle = left;
  g.beginPath();
  g.moveTo(x - w / 2, y);
  g.lineTo(x, y + h);
  g.lineTo(x, y + h + d);
  g.lineTo(x - w / 2, y + d);
  g.closePath();
  g.fill();

  // Right face.
  g.fillStyle = right;
  g.beginPath();
  g.moveTo(x + w / 2, y);
  g.lineTo(x, y + h);
  g.lineTo(x, y + h + d);
  g.lineTo(x + w / 2, y + d);
  g.closePath();
  g.fill();

  // Top face (diamond).
  g.fillStyle = flashing ? CUBE_TOP_FLASH : top;
  g.beginPath();
  g.moveTo(x, y - h);
  g.lineTo(x + w / 2, y);
  g.lineTo(x, y + h);
  g.lineTo(x - w / 2, y);
  g.closePath();
  g.fill();

  // Outline. Drawn as 1px lines so the iso edges read crisply against
  // adjacent cubes — without this the painted-cube cyan smears
  // into its neighbors.
  g.strokeStyle = CUBE_OUTLINE;
  g.lineWidth = 1;
  g.beginPath();
  g.moveTo(x, y - h);
  g.lineTo(x + w / 2, y);
  g.lineTo(x + w / 2, y + d);
  g.lineTo(x, y + h + d);
  g.lineTo(x - w / 2, y + d);
  g.lineTo(x - w / 2, y);
  g.closePath();
  g.stroke();
  g.beginPath();
  g.moveTo(x, y + h);
  g.lineTo(x, y + h + d);
  g.stroke();
  g.beginPath();
  g.moveTo(x, y - h);
  g.lineTo(x - w / 2, y);
  g.stroke();
  g.beginPath();
  g.moveTo(x, y - h);
  g.lineTo(x + w / 2, y);
  g.stroke();
  g.beginPath();
  g.moveTo(x, y + h);
  g.lineTo(x + w / 2, y);
  g.stroke();
  g.beginPath();
  g.moveTo(x, y + h);
  g.lineTo(x - w / 2, y);
  g.stroke();
}

function drawBean(
  g: CanvasRenderingContext2D,
  px: number,
  py: number,
  bobOffset: number
): void {
  // The bean sits ABOVE the cube's top-center. py is the cube top-center
  // Y; bean feet at py + BEAN_FOOT_OFFSET_Y. Bob is added to lift.
  const y = py + BEAN_FOOT_OFFSET_Y + bobOffset;
  // Body — two rounded halves with a vertical crease. We approximate
  // "rounded" with stacked rectangles at low res.
  // Outline.
  g.fillStyle = BEAN_OUTLINE;
  g.fillRect(px - BEAN_W / 2 - 1, y - BEAN_H, BEAN_W + 2, BEAN_H);
  // Body.
  g.fillStyle = BEAN_BODY;
  g.fillRect(px - BEAN_W / 2, y - BEAN_H, BEAN_W, BEAN_H);
  // Cream highlight on the upper-left.
  g.fillStyle = BEAN_HIGHLIGHT;
  g.fillRect(px - BEAN_W / 2 + 2, y - BEAN_H + 2, 4, 4);
  // Vertical crease down the center.
  g.fillStyle = BEAN_CREASE;
  g.fillRect(px, y - BEAN_H + 2, 1, BEAN_H - 4);
  // Eye nubs — two tiny outline dots up top so the bean has a "face."
  g.fillStyle = BEAN_OUTLINE;
  g.fillRect(px - 4, y - BEAN_H + 4, 1, 1);
  g.fillRect(px + 3, y - BEAN_H + 4, 1, 1);
}

function drawBeanSpinning(
  g: CanvasRenderingContext2D,
  px: number,
  py: number,
  spin: number
): void {
  // Falling bean — rotated around its centroid. We draw the bean in a
  // local frame and let the canvas transform handle the spin. Reads
  // as "the bean is tumbling off the pyramid edge."
  const y = py + BEAN_FOOT_OFFSET_Y - BEAN_H / 2;
  g.save();
  g.translate(px, y);
  g.rotate(spin);
  g.fillStyle = BEAN_OUTLINE;
  g.fillRect(-BEAN_W / 2 - 1, -BEAN_H / 2, BEAN_W + 2, BEAN_H);
  g.fillStyle = BEAN_BODY;
  g.fillRect(-BEAN_W / 2, -BEAN_H / 2, BEAN_W, BEAN_H);
  g.fillStyle = BEAN_HIGHLIGHT;
  g.fillRect(-BEAN_W / 2 + 2, -BEAN_H / 2 + 2, 4, 4);
  g.fillStyle = BEAN_CREASE;
  g.fillRect(0, -BEAN_H / 2 + 2, 1, BEAN_H - 4);
  g.restore();
}

function drawSnake(
  g: CanvasRenderingContext2D,
  px: number,
  py: number,
  facing: Dir | null,
  bobTicks: number
): void {
  // The snake is drawn as a head + 3 segment trail. Head leads in the
  // direction the snake last moved so the player reads chase intent.
  // The whole sprite anchors above the cube top-center, similar to
  // the bean but a touch lower so it reads as "on the cube, not
  // standing tall on it."
  const W = 14;
  const H = 12;
  const headY = py + BEAN_FOOT_OFFSET_Y - H / 2;
  // Direction-driven facing offset: head shifts toward the facing.
  let dx = 0;
  if (facing === 'NE' || facing === 'SE') dx = 3;
  else if (facing === 'NW' || facing === 'SW') dx = -3;

  // Body trail — 3 segments behind the head, slight vertical wobble
  // tied to bobTicks so it reads as alive between hops.
  const wobble = Math.sin((bobTicks / 12) * Math.PI * 2) * 0.6;
  for (let i = 0; i < 3; i++) {
    const segX = px - dx * (i + 1) * 0.7;
    const segY = headY + (i + 1) * 0.5 + wobble;
    g.fillStyle = SNAKE_DARK;
    g.fillRect(segX - 4, segY - 1, 8, H - 2);
    g.fillStyle = SNAKE_BODY;
    g.fillRect(segX - 3, segY, 6, H - 4);
  }

  // Head — bigger, with eye glint and forked tongue flick.
  g.fillStyle = SNAKE_DARK;
  g.fillRect(px + dx - W / 2 - 1, headY - 1, W + 2, H + 2);
  g.fillStyle = SNAKE_BODY;
  g.fillRect(px + dx - W / 2, headY, W, H);
  g.fillStyle = SNAKE_HIGHLIGHT;
  g.fillRect(px + dx - W / 2 + 2, headY + 2, W - 4, 2);
  // Eye — facing-aware (flips left/right with the head direction).
  g.fillStyle = SNAKE_EYE;
  const eyeX = px + dx + (dx >= 0 ? 2 : -3);
  g.fillRect(eyeX, headY + 3, 2, 2);
  // Tongue — flicks out on every other bob beat. Communicates "alive."
  const tongueOut = Math.floor(bobTicks / 16) % 2 === 0;
  if (tongueOut) {
    g.fillStyle = SNAKE_FORKED_TONGUE;
    const tongueX = px + dx + (dx >= 0 ? W / 2 : -W / 2 - 3);
    g.fillRect(tongueX, headY + H / 2, 3, 1);
  }
}

function drawDangerCard(g: CanvasRenderingContext2D, phaseTicks: number): void {
  // Fast-flashing red overlay + centered card. Reads as "incoming
  // threat" — distinct from the cyan PYRAMID CLEAR card.
  // Red tint pulses every 6 ticks; cuts through whatever else is on
  // the canvas underneath.
  const pulse = Math.floor(phaseTicks / 6) % 2 === 0;
  g.fillStyle = pulse ? 'rgba(255, 48, 48, 0.18)' : 'rgba(255, 48, 48, 0.08)';
  g.fillRect(0, 0, WIDTH, HEIGHT);

  // Card.
  const cw = 200;
  const ch = 50;
  const cx = (WIDTH - cw) / 2;
  const cy = (HEIGHT - ch) / 2;
  g.fillStyle = DANGER_BG;
  g.fillRect(cx, cy, cw, ch);
  g.strokeStyle = DANGER_BORDER;
  g.lineWidth = 2;
  g.strokeRect(cx + 0.5, cy + 0.5, cw - 1, ch - 1);

  g.font = '14px monospace';
  g.textBaseline = 'middle';
  g.textAlign = 'center';
  g.fillStyle = DANGER_TEXT;
  g.fillText('DANGER!', cx + cw / 2, cy + 18);

  g.font = '10px monospace';
  g.fillStyle = DANGER_SUB;
  g.fillText('SNAKE ON THE PYRAMID', cx + cw / 2, cy + 36);

  g.textAlign = 'left';
}

function drawGameOver(
  g: CanvasRenderingContext2D,
  finalScore: number,
  finalLevel: number,
  phaseTicks: number,
  lockTicks: number,
  won: boolean
): void {
  // CRT-glitch intro: scan-line noise wipes over the canvas, then
  // settles into a steady-state low-intensity glitch as the player
  // reads their score. The intro animates over GLITCH_INTRO_TICKS.
  const introProgress = Math.min(1, phaseTicks / GLITCH_INTRO_TICKS);

  // Black scrim ramps in from 0 → 0.7.
  g.fillStyle = `rgba(0, 0, 0, ${0.7 * introProgress})`;
  g.fillRect(0, 0, WIDTH, HEIGHT);

  // Horizontal scan-line tears at random heights — frequency drops
  // after the intro so it doesn't compete with the score readout.
  const tearCount = Math.floor(
    8 * introProgress * (phaseTicks < GLITCH_INTRO_TICKS ? 1 : 0.35)
  );
  for (let i = 0; i < tearCount; i++) {
    // Deterministic-from-phaseTicks pseudo-noise so the tears flicker
    // every few frames, not every single frame.
    const seed = (phaseTicks * 13 + i * 71) % 270;
    const tearY = seed;
    const tearH = 1 + ((phaseTicks + i) % 4);
    const colorPick = (phaseTicks + i) % 3;
    const tearColor =
      colorPick === 0
        ? 'rgba(0, 240, 200, 0.45)'
        : colorPick === 1
          ? 'rgba(255, 48, 136, 0.4)'
          : 'rgba(255, 234, 0, 0.35)';
    g.fillStyle = tearColor;
    g.fillRect(0, tearY, WIDTH, tearH);
  }

  // Color-channel split — paint thin cyan + magenta offset bars over
  // the score area so the text reads as "broken signal" even after
  // the intro settles.
  // Drawn underneath the actual score text so the legibility wins.

  // Panel — centered card, similar to level-clear but with a magenta
  // border and "GAME OVER" heading.
  const cw = 220;
  const ch = 90;
  const cx = (WIDTH - cw) / 2;
  const cy = (HEIGHT - ch) / 2;
  if (introProgress >= 0.5) {
    // Win variant: gold border + "TREASURE!" heading + "CARTRIDGE
    // COMPLETE" sub. Same panel layout as the loss path so the
    // muscle-memory of the GAME OVER read is preserved.
    const heading = won ? 'TREASURE!' : 'GAME OVER';
    const headingColor = won ? HUD_SCORE : '#ff3088';
    const borderColor = won ? HUD_SCORE : '#ff3088';
    const subText = won
      ? 'CARTRIDGE COMPLETE'
      : `REACHED PYRAMID ${finalLevel}`;

    g.fillStyle = CARD_BG;
    g.fillRect(cx, cy, cw, ch);
    g.strokeStyle = borderColor;
    g.lineWidth = 2;
    g.strokeRect(cx + 0.5, cy + 0.5, cw - 1, ch - 1);

    g.font = '14px monospace';
    g.textBaseline = 'middle';
    g.textAlign = 'center';
    g.fillStyle = headingColor;
    g.fillText(heading, cx + cw / 2, cy + 18);

    g.font = '10px monospace';
    g.fillStyle = HUD_LABEL;
    g.fillText('FINAL SCORE', cx + cw / 2, cy + 38);
    g.font = '14px monospace';
    g.fillStyle = HUD_SCORE;
    g.fillText(String(finalScore).padStart(6, '0'), cx + cw / 2, cy + 54);

    g.font = '8px monospace';
    g.fillStyle = won ? HUD_SCORE : CARD_SUB;
    g.fillText(subText, cx + cw / 2, cy + 70);

    // "PRESS Z" prompt — dim during the input lock, bright after.
    const promptLive = lockTicks <= 0;
    const promptBlink = Math.floor(phaseTicks / 24) % 2 === 0;
    if (promptLive && promptBlink) {
      g.fillStyle = CARD_BORDER;
      g.fillText('PRESS Z TO RESTART', cx + cw / 2, cy + 84);
    } else if (!promptLive) {
      g.fillStyle = ATTRACT_DIM;
      g.fillText('. . .', cx + cw / 2, cy + 84);
    }

    g.textAlign = 'left';
  }
}

function drawTreasureCard(
  g: CanvasRenderingContext2D,
  finalScore: number,
  elapsed: number
): void {
  // 4-second celebratory cinematic before the panel hands off to
  // game-over. Gold/cyan starburst around a central TREASURE! card
  // with the final score. Star rays rotate slowly; the score crawls
  // in via a typewriter effect over the first ~1s.
  // Background — black scrim to focus attention.
  g.fillStyle = 'rgba(0, 0, 0, 0.7)';
  g.fillRect(0, 0, WIDTH, HEIGHT);

  // Rotating starburst rays. Cheap and effective — long thin
  // triangles emanating from screen center.
  const rayCount = 16;
  const cx0 = WIDTH / 2;
  const cy0 = HEIGHT / 2;
  const rotation = (elapsed / 60) * Math.PI; // half-revolution per second
  g.save();
  g.translate(cx0, cy0);
  g.rotate(rotation);
  for (let i = 0; i < rayCount; i++) {
    const a = (i / rayCount) * Math.PI * 2;
    const dx = Math.cos(a);
    const dy = Math.sin(a);
    // Alternate gold + cyan rays for arcade flash.
    g.fillStyle = i % 2 === 0 ? HUD_SCORE : CARD_BORDER;
    g.globalAlpha = 0.18;
    g.beginPath();
    g.moveTo(0, 0);
    g.lineTo(dx * 400 - dy * 8, dy * 400 + dx * 8);
    g.lineTo(dx * 400 + dy * 8, dy * 400 - dx * 8);
    g.closePath();
    g.fill();
  }
  g.restore();

  // Centered panel.
  const cw = 240;
  const ch = 100;
  const cx = (WIDTH - cw) / 2;
  const cy = (HEIGHT - ch) / 2;
  g.fillStyle = CARD_BG;
  g.fillRect(cx, cy, cw, ch);
  g.strokeStyle = HUD_SCORE;
  g.lineWidth = 2;
  g.strokeRect(cx + 0.5, cy + 0.5, cw - 1, ch - 1);

  // Burst-scale TREASURE! heading on the first ~12 ticks.
  const burstFrac = Math.min(1, elapsed / 12);
  const scale = 0.5 + 0.5 * burstFrac;
  g.save();
  g.translate(cx + cw / 2, cy + 22);
  g.scale(scale, scale);
  g.font = '18px monospace';
  g.textBaseline = 'middle';
  g.textAlign = 'center';
  g.fillStyle = HUD_SCORE;
  g.fillText('TREASURE!', 0, 0);
  g.restore();

  // CARTRIDGE COMPLETE sub.
  g.font = '10px monospace';
  g.textBaseline = 'middle';
  g.textAlign = 'center';
  g.fillStyle = CARD_BORDER;
  g.fillText('CARTRIDGE COMPLETE', cx + cw / 2, cy + 46);

  // Final score with a typewriter reveal — chars appear over the
  // first 60 ticks (1s) for a "cha-ching" payoff. After that the
  // full number is locked.
  const scoreText = String(finalScore).padStart(6, '0');
  const charsRevealed = Math.min(scoreText.length, Math.floor(elapsed / 10));
  const revealed = scoreText.slice(0, charsRevealed).padEnd(scoreText.length, ' ');
  g.font = '16px monospace';
  g.fillStyle = HUD_SCORE;
  g.fillText(revealed, cx + cw / 2, cy + 72);

  g.font = '6px monospace';
  g.fillStyle = HUD_LABEL;
  g.fillText('+10000 WIN BONUS', cx + cw / 2, cy + 90);

  g.textAlign = 'left';
}

function drawLevelCard(
  g: CanvasRenderingContext2D,
  _level: number,
  lastRoundBonus: number,
  lastSpeedBonus: number
): void {
  // Centered card with cyan border and gold "PYRAMID CLEAR!" heading.
  // Now includes a per-round bonus breakdown so the player sees why
  // they got more points than just paints + clear.
  const cw = 220;
  const ch = lastSpeedBonus > 0 ? 96 : 80;
  const cx = (WIDTH - cw) / 2;
  const cy = (HEIGHT - ch) / 2;
  // Scrim behind the card so it pops over the pyramid.
  g.fillStyle = 'rgba(0,0,0,0.55)';
  g.fillRect(0, 0, WIDTH, HEIGHT);
  // Card body.
  g.fillStyle = CARD_BG;
  g.fillRect(cx, cy, cw, ch);
  g.strokeStyle = CARD_BORDER;
  g.lineWidth = 2;
  g.strokeRect(cx + 0.5, cy + 0.5, cw - 1, ch - 1);
  // Heading.
  g.font = '14px monospace';
  g.textBaseline = 'middle';
  g.textAlign = 'center';
  g.fillStyle = CARD_TEXT;
  g.fillText('PYRAMID CLEAR!', cx + cw / 2, cy + 18);

  // Bonus breakdown. CLEAR is constant LEVEL_CLEAR_BONUS, LIVES is
  // lives×500, SPEED is +500 if cleared inside the threshold.
  g.font = '8px monospace';
  let lineY = cy + 36;
  g.fillStyle = CARD_SUB;
  g.fillText(`CLEAR BONUS  +${LEVEL_CLEAR_BONUS}`, cx + cw / 2, lineY);
  lineY += 10;
  if (lastRoundBonus > 0) {
    g.fillText(`LIVES BONUS  +${lastRoundBonus}`, cx + cw / 2, lineY);
    lineY += 10;
  }
  if (lastSpeedBonus > 0) {
    g.fillStyle = HUD_SCORE;
    g.fillText(`SPEED BONUS  +${lastSpeedBonus}`, cx + cw / 2, lineY);
    lineY += 10;
  }
  g.textAlign = 'left';
}

function drawGetReadyCard(
  g: CanvasRenderingContext2D,
  level: number,
  elapsed: number,
  isZoneTransition: boolean
): void {
  // Card lives for GET_READY_TICKS (120). We split into thirds for the
  // 3-2-1 countdown so the numeral changes feel like real countdown
  // beats rather than a constant card.
  const segment = Math.floor(GET_READY_TICKS / 3);
  let countdown: number;
  if (elapsed < segment) countdown = 3;
  else if (elapsed < segment * 2) countdown = 2;
  else countdown = 1;

  // Scrim behind the card.
  g.fillStyle = 'rgba(0, 0, 0, 0.55)';
  g.fillRect(0, 0, WIDTH, HEIGHT);

  const cw = 240;
  const ch = isZoneTransition ? 100 : 80;
  const cx = (WIDTH - cw) / 2;
  const cy = (HEIGHT - ch) / 2;

  // BETA-tinted border on zone-transition, cyan otherwise.
  const border = isZoneTransition ? BETA_CUBE_TOP_PAINTED : CARD_BORDER;
  g.fillStyle = CARD_BG;
  g.fillRect(cx, cy, cw, ch);
  g.strokeStyle = border;
  g.lineWidth = 2;
  g.strokeRect(cx + 0.5, cy + 0.5, cw - 1, ch - 1);

  g.font = '12px monospace';
  g.textBaseline = 'middle';
  g.textAlign = 'center';
  g.fillStyle = CARD_TEXT;
  g.fillText(`GET READY — PYRAMID ${level}`, cx + cw / 2, cy + 18);

  if (isZoneTransition) {
    g.font = '10px monospace';
    g.fillStyle = border;
    g.fillText('ZONE: PYRAMID BETA', cx + cw / 2, cy + 36);
  }

  // Big countdown numeral. Scales up briefly as each new number
  // appears — easy way to make the count feel like a beat.
  const intoSegment = elapsed - segment * (3 - countdown);
  const segmentProgress = intoSegment / segment;
  // Burst scale on appearance: starts large, settles to 1.
  const scale = 1 + Math.max(0, 0.4 - segmentProgress * 0.8);
  g.save();
  g.translate(cx + cw / 2, cy + ch - 24);
  g.scale(scale, scale);
  g.font = '28px monospace';
  g.fillStyle = HUD_SCORE;
  g.fillText(String(countdown), 0, 0);
  g.restore();

  g.textAlign = 'left';
}

function drawRotateWipe(g: CanvasRenderingContext2D, progress: number): void {
  // CRT-style horizontal wipe over the pyramid. As progress goes
  // 0 → 1 a bright scan line sweeps top-to-bottom and the cubes
  // below the line dim. Suggests "the pyramid is reconfiguring."
  const scanY = progress * HEIGHT;
  // Dim everything below the scan line.
  g.fillStyle = 'rgba(0, 0, 0, 0.45)';
  g.fillRect(0, scanY, WIDTH, HEIGHT - scanY);
  // Bright scan line itself.
  g.fillStyle = CARD_BORDER;
  g.fillRect(0, scanY - 1, WIDTH, 2);
  // Soft glow above the line.
  g.fillStyle = 'rgba(0, 240, 200, 0.18)';
  g.fillRect(0, scanY - 6, WIDTH, 6);
}

function drawAttractBanner(g: CanvasRenderingContext2D, worldTicks: number): void {
  // Bottom-center "DEMO PLAY" pill that blinks slowly so the player
  // reads "I haven't given input — the cabinet is showing me a demo."
  const blink = Math.floor(worldTicks / 30) % 2 === 0;
  if (!blink) return;
  const text = 'DEMO PLAY';
  g.font = '8px monospace';
  g.textBaseline = 'middle';
  g.textAlign = 'center';
  const w = g.measureText(text).width + 16;
  const h = 14;
  const x = (WIDTH - w) / 2;
  const y = HEIGHT - 20;
  g.fillStyle = CARD_BG;
  g.fillRect(x, y, w, h);
  g.strokeStyle = ATTRACT_TITLE;
  g.lineWidth = 1;
  g.strokeRect(x + 0.5, y + 0.5, w - 1, h - 1);
  g.fillStyle = ATTRACT_PROMPT;
  g.fillText(text, WIDTH / 2, y + h / 2);
  g.textAlign = 'left';
}
